import type { NormalizedCacheObject } from '@apollo/client';
import { ApolloClient, ApolloLink, useApolloClient } from '@apollo/client';
import { useCallback } from 'react';
import authTokenUtil from '../../account/authTokenUtil';
import checkVersion from '../checkVersion';
import authLink from './authLink';
import cache, { tryRestore } from './cache';
import getErrorLink from './getErrorLink';
import getTransportLink from './getTransportLink';
import parseJsonLink from './parseJsonLink';
import type { ValidationErrorHandler } from '../../utils/error/handleUnknownApolloErrors';

const getLinks = async ({
  handleValidationError,
}: {
  handleValidationError: ValidationErrorHandler;
}) => {
  const links = [
    authLink,
    getErrorLink({ handleValidationError }),
    parseJsonLink,
    await getTransportLink(),
  ];

  if (process.env.NODE_ENV !== 'production') {
    const apolloLinkLogger = (await import('apollo-link-logger')).default;
    links.unshift(apolloLinkLogger);
  }

  return links;
};

export function useResetApolloLinks() {
  const apolloClient = useApolloClient();
  return useCallback(async () => {
    const links = await getLinks({
      handleValidationError: () => checkVersion(apolloClient),
    });
    apolloClient.setLink(ApolloLink.from(links));
  }, [apolloClient]);
}

let client: ApolloClient<NormalizedCacheObject>;

export default async function getApolloClient() {
  if (client) return client;

  if (await authTokenUtil.getAsync()) {
    await tryRestore();
  }

  const links = await getLinks({
    handleValidationError: () => checkVersion(client),
  });

  client = new ApolloClient({
    cache,
    link: ApolloLink.from(links),
    assumeImmutableResults: true,
    connectToDevTools: process.env.NODE_ENV !== 'production',
    defaultOptions: {
      watchQuery: {
        fetchPolicy: 'cache-and-network',
        // After Apollo 3.1, 'cache-and-network` means whenever the cached value changes, a new network request is triggered.
        // in order to avoid this unnecessary effect (which could even lead to infinite loops), nextFetchPolicy is needed.
        // recommended setting to from https://github.com/apollographql/apollo-client/pull/6712#issuecomment-679446404
        nextFetchPolicy(lastFetchPolicy) {
          if (
            lastFetchPolicy === 'cache-and-network' ||
            lastFetchPolicy === 'network-only'
          ) {
            return 'cache-first';
          }
          return lastFetchPolicy;
        },
      },
    },
  });

  return client;
}
